import React, { useEffect, useState, useRef } from "react"
import { NavLink } from "react-router-dom"
import { db } from "../../../utils/firebase"
import moment from "moment"
import { CopyIcon, CheckIcon } from "../../../utils/svgs"

import WindowCard from "../../../components/ui/window-card/window-card"

/**
 * Returns "x days ago", or "today", or absolute date for older entries
 */
const relativeTime = (seconds) => {
	if (!seconds) return null
	const m = moment(seconds, "X")
	const days = moment().diff(m, "days")
	if (days <= 30) return m.fromNow()
	return m.format("MMM D, YYYY")
}

/**
 * Format the client's tenure as a human-readable duration ("2 years",
 * "8 months", "3 days") plus the absolute join date for tooltip use.
 */
const formatMemberSince = (seconds) => {
	if (!seconds) return null
	const m = moment(seconds, "X")
	const date = m.format("MMM YYYY")
	const years = moment().diff(m, "years")
	const months = moment().diff(m, "months")
	const days = moment().diff(m, "days")

	let duration
	if (years >= 1) duration = years === 1 ? "1 year" : `${years} years`
	else if (months >= 1) duration = months === 1 ? "1 month" : `${months} months`
	else if (days <= 0) duration = "today"
	else duration = days === 1 ? "1 day" : `${days} days`

	return { date, duration }
}

/**
 * Pick the best available revenue figure from a booking document.
 * Order of preference matches the booking model's own fallbacks:
 *  1. margins.customer_price — canonical revenue (sum of price_lines)
 *  2. total_cost — used by direct bookings created outside the enquiry flow
 *  3. enquiry.quoted — original quoted price stamped onto the booking
 */
const bookingRevenue = (b) =>
	Number(b?.margins?.customer_price) ||
	Number(b?.total_cost) ||
	Number(b?.enquiry?.quoted) ||
	0

const formatGBP = (value) => `£${Math.round(value || 0).toLocaleString()}`

/**
 * Inline button that copies its value to the clipboard. Shows a subtle
 * copy icon on hover and morphs to a green check briefly on success.
 *
 * Falls back to a no-op (just renders the text) on the rare browser
 * without `navigator.clipboard.writeText` — in practice everything we
 * support has it.
 */
function CopyableValue({ value, label }) {
	const [copied, setCopied] = useState(false)
	const timerRef = useRef(null)

	useEffect(() => {
		// Clear any pending reset timer when this row unmounts so we
		// don't call setState on an unmounted component.
		return () => {
			if (timerRef.current) clearTimeout(timerRef.current)
		}
	}, [])

	const handleCopy = async () => {
		if (!value || !navigator.clipboard?.writeText) return
		try {
			await navigator.clipboard.writeText(value)
			setCopied(true)
			if (timerRef.current) clearTimeout(timerRef.current)
			timerRef.current = setTimeout(() => setCopied(false), 1400)
		} catch (err) {
			console.error("Failed to copy to clipboard:", err)
		}
	}

	return (
		<button
			type="button"
			className={["enquiry-card-copyable", copied ? "is-copied" : ""].join(" ")}
			onClick={handleCopy}
			title={copied ? "Copied" : `Click to copy ${label}`}
			aria-label={copied ? `${label} copied to clipboard` : `Copy ${label}`}>
			<span className="enquiry-card-copyable-text">{value}</span>
			<span className="enquiry-card-copyable-state" aria-hidden="true">
				{copied ? <CheckIcon /> : <CopyIcon />}
			</span>
		</button>
	)
}

/**
 * Right-rail card showing prior activity for the client behind this enquiry
 */
export default function ClientCard({ clientID, currentEnquiryID }) {
	const [client, setClient] = useState(null)
	const [priorEnquiries, setPriorEnquiries] = useState({ count: 0, mostRecent: null })
	const [priorBookings, setPriorBookings] = useState({ count: 0, mostRecent: null, totalValue: 0 })

	useEffect(() => {
		if (!clientID) return
		const unsubscribe = db.doc(`clients/${clientID}`).onSnapshot((doc) => {
			if (!doc.exists) return
			setClient(doc.data())
		})
		return () => unsubscribe()
	}, [clientID])

	useEffect(() => {
		if (!clientID) return

		// Prior enquiries follow the conversion stats model: active + removed
		// enquiries, with converted bookings already represented by their
		// archived enquiry record.
		Promise.all([
			db
				.collection("enquiries")
				.where("client", "==", clientID)
				.get()
				.then((snap) =>
					snap.docs
						.filter((d) => d.id !== currentEnquiryID)
						.map((d) => d.data())
						.filter((e) => e.archived_from_stats !== true)
				),
			db
				.collection("enquiries_removed")
				.where("enquiry.client", "==", clientID)
				.get()
				.then((snap) =>
					snap.docs
						.map((d) => d.data())
						.filter((d) => d.archived_from_stats !== true)
						.map((d) => d.enquiry || {})
				),
		]).then(([activeEnquiries, removedEnquiries]) => {
			const enquiries = [...activeEnquiries, ...removedEnquiries]
			const mostRecent = enquiries
				.map((e) => e.created?.seconds || 0)
				.sort((a, b) => b - a)[0]
			setPriorEnquiries({ count: enquiries.length, mostRecent })
		})

		// Prior bookings — also tally total revenue across all of them
		// for the lifetime-value figure.
		db.collection("bookings")
			.where("client", "==", clientID)
			.get()
			.then((snap) => {
				const docs = snap.docs.map((d) => d.data())
				const mostRecent = docs
					.map((b) => b.created?.seconds || 0)
					.sort((a, b) => b - a)[0]
				const totalValue = docs.reduce((sum, b) => sum + bookingRevenue(b), 0)
				setPriorBookings({ count: snap.size, mostRecent, totalValue })
			})
	}, [clientID, currentEnquiryID])

	const conversion = priorEnquiries.count > 0 ? Math.round((priorBookings.count / priorEnquiries.count) * 100) : null

	const lastContactSeconds = Math.max(priorEnquiries.mostRecent || 0, priorBookings.mostRecent || 0)
	const memberSince = formatMemberSince(client?.created?.seconds)

	return (
		<WindowCard title="Client">
			<div className="enquiry-card-body">
				<div className="enquiry-card-row">
					<span className="enquiry-card-label">Name</span>
					<span className="enquiry-card-value">
						{clientID && client ? (
							<NavLink to={`/client/${clientID}`}>
								{client.first_name || ""} {client.last_name || ""}
							</NavLink>
						) : (
							<small className="no-value">No client</small>
						)}
					</span>
				</div>

				<div className="enquiry-card-row">
					<span className="enquiry-card-label">Email</span>
					<span className="enquiry-card-value">
						{client?.email ? (
							<CopyableValue value={client.email} label="email" />
						) : (
							<small className="no-value">—</small>
						)}
					</span>
				</div>

				<div className="enquiry-card-row">
					<span className="enquiry-card-label">Phone</span>
					<span className="enquiry-card-value">
						{client?.phone ? (
							<CopyableValue value={client.phone} label="phone number" />
						) : (
							<small className="no-value">—</small>
						)}
					</span>
				</div>

				<div className="enquiry-card-row">
					<span className="enquiry-card-label">Member since</span>
					<span
						className="enquiry-card-value"
						title={memberSince ? `Joined ${memberSince.date}` : undefined}>
						{memberSince ? memberSince.duration : <small className="no-value">—</small>}
					</span>
				</div>

				<div className="enquiry-card-divider" />

				<div className="enquiry-card-row">
					<span className="enquiry-card-label">Prior enquiries</span>
					<span className="enquiry-card-value">{priorEnquiries.count}</span>
				</div>

				<div className="enquiry-card-row">
					<span className="enquiry-card-label">Prior bookings</span>
					<span className="enquiry-card-value">{priorBookings.count}</span>
				</div>

				<div className="enquiry-card-row">
					<span className="enquiry-card-label">Lifetime value</span>
					<span className="enquiry-card-value">
						{priorBookings.count > 0 ? (
							formatGBP(priorBookings.totalValue)
						) : (
							<small className="no-value">—</small>
						)}
					</span>
				</div>

				<div className="enquiry-card-row">
					<span className="enquiry-card-label">Conversion</span>
					<span className="enquiry-card-value">
						{conversion !== null ? `${conversion}%` : <small className="no-value">—</small>}
					</span>
				</div>

				<div className="enquiry-card-row">
					<span className="enquiry-card-label">Last contact</span>
					<span className="enquiry-card-value">
						{lastContactSeconds ? relativeTime(lastContactSeconds) : <small className="no-value">—</small>}
					</span>
				</div>
			</div>
		</WindowCard>
	)
}
